import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { UserListItemDto } from "@/api/types";
import { FormSearchableSelect } from "@/components/Form/FormSearchableSelect";
import { User } from "@/components/User/User";
import { UserDetailsContent } from "@/components/UserDetailsContent/UserDetailsContent";
import { Box } from "@/design-system/Box";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { addressTagsToDictionary } from "@/helpers/address";
import { isDefined } from "@/helpers/util";
import { useUserQueries } from "@/queries/users/queryOptions";
import { useTranslation } from "@/translations";

import type { CreateTicketModalFormValues } from "../types";

const SelectReporterStepContent = (): React.ReactNode => {
  const { t } = useTranslation();
  const form = useFormContext<CreateTicketModalFormValues>();

  const currReporter = useWatch({
    name: "reporter",
    control: form.control,
  });

  useEffect(() => {
    if (currReporter) {
      form.setValue("phoneNumber", currReporter.phoneNumber);
    }
  }, [currReporter, form]);

  const userQueries = useUserQueries();
  const {
    data: potentialReporters = [],
    isLoading: isLoadingPotentialReporters,
    error: errorPotentialReporters,
  } = useQuery({
    ...userQueries.list({
      params: {
        Offset: 0,
        Limit: 10000,
        Statuses: ["invited", "onboarded"],
      },
    }),
  });
  const {
    data: currReporterDetails,
    isFetching: isFetchingCurrReporterDetails,
    error: errorCurrReporterDetails,
  } = useQuery({
    ...userQueries.details({
      userId: currReporter?.id || "",
    }),
    enabled: isDefined(currReporter?.id),
  });

  const error = errorPotentialReporters || errorCurrReporterDetails;
  if (error) {
    throw new Error(error.message);
  }

  return (
    <section className="flex h-full flex-col gap-space-md">
      <FormField label={t("page.resident-tickets.create-modal.form.reporter.label")} required>
        <FormSearchableSelect<CreateTicketModalFormValues, UserListItemDto>
          name="reporter"
          placeholder={t("page.resident-tickets.create-modal.form.reporter.placeholder")}
          items={potentialReporters}
          keySelector={(x) => x.id}
          searchableFieldSelector={(x) => [x.fullName, x.locatedAt || ""]}
          renderOption={(x) => <User user={x} renderSubtitle={() => x.locatedAt} shouldNavigate={false} />}
          renderSelected={(x) => x.fullName}
          rules={{
            required: t("components.form.error.required-generic"),
          }}
          disabled={errorPotentialReporters || isLoadingPotentialReporters || isFetchingCurrReporterDetails}
          usePortal
        />
      </FormField>
      {isFetchingCurrReporterDetails && (
        <section className="flex flex-1 items-center">
          <FullSizeLoader size="sm" noDelay />
        </section>
      )}
      {!isFetchingCurrReporterDetails && !errorCurrReporterDetails && currReporterDetails && (
        <Box radius="md" background="secondary" padding="md">
          <UserDetailsContent
            user={currReporterDetails}
            address={addressTagsToDictionary(currReporterDetails.addressTags)}
            showAddressDetails
          />
        </Box>
      )}
    </section>
  );
};

export const CreateTicketModalAdminSteps = {
  SelectReporter: SelectReporterStepContent,
};
